import { useTranslation } from "react-i18next";
import React, { useEffect, useMemo, useState } from "react";
import * as Ariakit from "@ariakit/react";
import { useDebounce } from "@uidotdev/usehooks";
import {
  CalendarDays,
  ChevronDown,
  GraduationCap,
  Search,
  TriangleAlert,
  User,
} from "lucide-react";
import styled from "styled-components";

import { Color } from "../../constants/color";
import { BreakpointsQuery } from "../../constants/device";

import { useCertificatesDataset } from "../../features/certificate/useCertificatesDataset";
import {
  filtraPerSede,
  filtraPerStato,
  filtraPerTesto,
  filtraPerTitoloCorso,
  nomeCorso,
  gruppiPerDipendente,
  opzioniSede,
  statoDaCodice,
} from "../../features/certificate/pipeline";
import {
  BadgeStatoAttestato,
  InfoPrenotazione,
  ScaricaAttestato,
} from "./riga-attestato";
import { StatoVuoto } from "./stato-vuoto";
import Suggerimento from "../tooltip/suggerimento";

import {
  Select,
  SelectItem,
  SelectLabel,
  SelectProvider,
} from "../form/select";
import { DocumentData, GruppoDipendente } from "../../types";
import { useAppDispatch } from "../../app/hooks";
import { vaiAllaPrenotazione } from "../../features/employee/prenotazione";
import { BarLoader } from "react-spinners";

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  padding: 2rem 4rem;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: end;
  gap: 2rem;
  @media ${BreakpointsQuery.tablet} {
    justify-content: center;
    gap: 1rem;
  }
`;

const SearchInput = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  & label {
    margin-left: 0.5rem;
    font-family:
      Avenir Next,
      sans-serif;
    font-weight: bold;
    color: ${Color.LIGHT_BLUE};
  }
  & input {
    border: 1px solid ${Color.LIGHT_BLUE};
    border-radius: 1rem;
    padding: 0.2rem 1rem;
    width: 100%;
  }
`;

const SearchButton = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${Color.LIGHT_BLUE};
  color: ${Color.WHITE};
  top: 0;
  right: 0;
  bottom: 0;
  width: 2.5rem;
  border-radius: 0 1rem 1rem 0;
`;

// Voci della select STATO, con i codici di `statoDaCodice` nella pipeline.
// 0 = nessun filtro. Il parametro `?status=N` dell'indirizzo appartiene alla
// tab Scadenza (ci arrivano i KPI): questa tab parte sempre da TUTTI, anche
// dopo un refresh su un indirizzo che lo porta.
// In questa tab i mancanti non sono un filtro: un corso mancante non ancora
// assegnato non ha discente e non compare.
// "In scadenza" vale sei mesi (`CertificateStatusRule` del backend): qui non
// c'e' il selettore 1/2/6 mesi della tab Scadenza, quindi lo dice la voce.
const statoAttestati: { [key: number]: string } = {
  0: "TUTTI",
  1: "VALIDO",
  2: "IN SCADENZA (ENTRO 6 MESI)",
  3: "SCADUTO",
};

type FiltersType = {
  discente: string;
  sede: string;
  stato: number;
};

export default function RicercaPerDipendente() {
  const { t } = useTranslation();
  // Pipeline condivisa: lista nell'ordine canonico del backend con lo stato
  // già calcolato, sedi di lavoro e catalogo, recuperati una volta per visita
  // alla rotta e condivisi con le altre tab.
  const {
    attestati: tuttiAttestati,
    sediDiLavoro,
    catalogo,
    loading,
  } = useCertificatesDataset();

  const sedeAttestati = useMemo(
    () => opzioniSede(sediDiLavoro, t("attestati-sede-tutte")),
    [sediDiLavoro, t]
  );

  const [filters, setFilters] = useState<FiltersType>({
    discente: "",
    sede: "",
    stato: 0,
  });

  const debouncedFilters = useDebounce(filters, 300);

  // Filtri in pagina sul set condiviso e raggruppamento per dipendente: gli
  // accordion sono in ordine di cognome, nome e codice fiscale, le righe
  // dentro ogni accordion restano nell'ordine del backend. Nessun
  // riordinamento dei record.
  const gruppi = useMemo(() => {
    let righe = filtraPerTesto(tuttiAttestati, debouncedFilters.discente);
    righe = filtraPerStato(righe, statoDaCodice(debouncedFilters.stato));
    righe = filtraPerSede(righe, debouncedFilters.sede);
    return gruppiPerDipendente(righe);
  }, [tuttiAttestati, debouncedFilters]);

  // Un risultato vuoto si spiega in due modi diversi: o i filtri escludono
  // tutto, o non c'e' ancora nulla da mostrare.
  const filtriAttivi =
    !!debouncedFilters.discente ||
    !!debouncedFilters.sede ||
    debouncedFilters.stato !== 0;

  const selectSede = Ariakit.useSelectStore({
    value: filters.sede,
    setValue: (value) => {
      setFilters((prev) => ({ ...prev, sede: value }));
    },
  });

  const selectStato = Ariakit.useSelectStore({
    value: `${filters.stato}`,
    setValue: (value) => {
      setFilters((prev) => ({ ...prev, stato: +value }));
    },
  });

  return (
    <Layout>
      <SearchContainer>
        <SearchInput>
          <label htmlFor="searchDiscente">COGNOME O NOME</label>
          <div
            style={{
              position: "relative",
              minWidth: "400px",
            }}
          >
            <input
              name="searchDiscente"
              type="text"
              placeholder="Ricerca cognome o nome"
              aria-label="Ricerca cognome o nome"
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, discente: e.target.value }))
              }
            />
            <SearchButton>
              <Search color={Color.WHITE} size={16} width={16} />
            </SearchButton>
          </div>
        </SearchInput>
        <SelectProvider store={selectSede}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.1rem",
            }}
          >
            <SelectLabel
              style={{
                paddingLeft: "0.5rem",
              }}
            >
              SEDE AZIENDALE
            </SelectLabel>
            <Select
              style={{
                minWidth: "200px",
              }}
              renderValue={(value) => sedeAttestati[`${value}`]}
            >
              {Object.entries(sedeAttestati).map(([key, value]) => (
                <SelectItem key={`sedeAttestato_${key}`} value={key}>
                  {value}
                </SelectItem>
              ))}
            </Select>
          </div>
        </SelectProvider>
        <SelectProvider store={selectStato}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "0.1rem",
            }}
          >
            <SelectLabel
              style={{
                paddingLeft: "0.5rem",
              }}
            >
              STATO
            </SelectLabel>
            <Select
              style={{
                minWidth: "200px",
              }}
              renderValue={(value) => statoAttestati[+value]}
            >
              {Object.entries(statoAttestati).map(([key, value]) => (
                <SelectItem key={`statoAttestato_${key}`} value={key}>
                  {value}
                </SelectItem>
              ))}
            </Select>
          </div>
        </SelectProvider>
      </SearchContainer>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "2rem",
          width: "78rem",
        }}
      >
        {gruppi.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              marginBottom: "-1.5rem",
              fontSize: ".8rem",
              fontWeight: "bold",
              color: Color.LIGHT_BLUE,
            }}
          >
            <p
              style={{
                width: "110px",
              }}
            ></p>
            <p
              style={{
                width: "450px",
              }}
            >
              COGNOME E NOME
            </p>
            <p
              style={{
                width: "230px",
              }}
            >
              CODICE FISCALE
            </p>
            <p
              style={{
                width: "180px",
              }}
            >
              SEDE AZIENDALE
            </p>
            <p>ATTESTATI</p>
          </div>
        )}
        {loading ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
              fontSize: "1.5rem",
              marginTop: "4rem",
            }}
          >
            <p>Caricamento dipendenti...</p>
            <BarLoader width="23ch" loading={loading} />
          </div>
        ) : gruppi.length === 0 ? (
          <StatoVuoto
            icona={User}
            titolo={
              filtriAttivi
                ? t("attestati-tab-dipendente-vuoto-filtri")
                : t("attestati-tab-dipendente-vuoto")
            }
            suggerimento={
              filtriAttivi
                ? t("attestati-tab-dipendente-vuoto-filtri-suggerimento")
                : t("attestati-tab-dipendente-vuoto-suggerimento")
            }
          />
        ) : (
          <>
            {gruppi.map((gruppo) => (
              <div
                key={gruppo.cf}
                style={{
                  width: "100%",
                  filter: "drop-shadow(4px -4px 5px var(--shadow800))",
                }}
              >
                <DipendenteAccordion gruppo={gruppo} catalogo={catalogo} />
              </div>
            ))}
          </>
        )}
      </div>
    </Layout>
  );
}

const Disclosure = styled(Ariakit.Disclosure)`
  border-radius: 0.8rem;
  background-color: ${Color.WHITE};
  width: 100%;
  position: relative;
  overflow: hidden;
  border: none;
  padding: 0.5rem 1.5rem;

  &:before {
    position: absolute;
    content: "";
    top: 0;
    left: 0;
    bottom: 0;
    width: 1rem;
    background-color: ${Color.LIGHT_BLUE};
  }

  & p[role='button'] {
    position: absolute;
    right: 0;
    top: 12px;
    bottom: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 2rem;
    border-radius: 0.8rem 0 0 0.8rem;
    background-color: ${Color.GREEN};
    color: ${Color.WHITE};
    font-weight: bold;
`;

const DisclosureContent = styled(Ariakit.DisclosureContent)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  width: calc(100% - 0.8rem);
  margin: -8px auto 0 auto;

  background-color: ${Color.CERAMIC};
  border-bottom-left-radius: 1.2rem;
  border-bottom-right-radius: 1.2rem;
  padding: 1.6rem 2rem;
  ::-webkit-scrollbar {
    position: relative;
    display: flex;
    left: 4rem;
    width: 0.6rem;
    border-radius: 4rem;
  }
  ::-webkit-scrollbar-button {
    display: none;
  }
  ::-webkit-scrollbar-track {
    border-radius: 4rem;
    background-color: ${Color.LIGHT_BLUE};
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 4rem;
    background-color: ${Color.LIGHT_BLUE};
  }
  ::-webkit-scrollbar-track-piece {
    border-radius: 4rem;
    background-color: ${Color.GREY};
  }
`;

const TableAttestati = styled.div`
  display: grid;
  grid-template-columns: auto 150px 200px 150px 120px 60px;
  align-items: center;
  width: 100%;
  padding: 0.5rem 1rem;
  font-size: 0.8rem;
  font-weight: bold;
`;

const TableAttestatiHeader = styled(TableAttestati)`
  text-transform: uppercase;
  color: ${Color.LIGHT_BLUE};
`;

const TableAttestatiRow = styled(TableAttestati)`
  border: 1px solid ${Color.GREY};
  border-radius: 16px;
  text-transform: uppercase;
  color: ${Color.DARK_GREY};
  background-color: ${Color.LIGHT_GREY};
  min-height: 3.2rem;
`;

function DipendenteAccordion({
  gruppo,
  catalogo,
}: {
  gruppo: GruppoDipendente;
  catalogo: Map<string, string>;
}) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const discente = gruppo.rappresentativo.metadata;

  const [searchCorso, setSearchCorso] = useState("");
  const debouncedSearchCorso = useDebounce(searchCorso, 300);

  // Il contenuto (righe attestati) viene montato solo alla prima apertura e poi
  // mantenuto: evita di renderizzare i dettagli di tutti i dipendenti in pagina
  // (con 90+ dipendenti erano migliaia di nodi DOM sempre presenti, con paint
  // pesante). Rende molto più leggero il primo render e le interazioni.
  const disclosure = Ariakit.useDisclosureStore();
  const isOpen = Ariakit.useStoreState(disclosure, "open");
  const [hasOpened, setHasOpened] = useState(false);
  useEffect(() => {
    if (isOpen && !hasOpened) {
      setHasOpened(true);
    }
  }, [isOpen, hasOpened]);

  // Ricerca sul nome del corso che la riga mostra; le righe restano
  // nell'ordine del backend.
  const filteredAttestati = useMemo(
    () =>
      filtraPerTitoloCorso(gruppo.attestati, debouncedSearchCorso, catalogo),
    [gruppo.attestati, debouncedSearchCorso, catalogo]
  );

  function prenota(a: DocumentData) {
    vaiAllaPrenotazione(
      dispatch,
      [gruppo.cf.toUpperCase()],
      a.reservationCode || a.metadata.codice_corso
    );
  }

  return (
    <>
      <Ariakit.DisclosureProvider store={disclosure}>
        <Disclosure>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <div
              style={{
                width: "450px",
                display: "flex",
                alignItems: "center",
                gap: "2rem",
                fontFamily: "Avenir Next Bold",
              }}
            >
              <User color={Color.LIGHT_BLUE} size={48} />
              <p
                style={{
                  fontSize: "1.5rem",
                  fontWeight: "bold",
                  color: Color.LIGHT_BLUE,
                  textAlign: "left",
                  lineHeight: "1.2",
                }}
              >
                {`${discente.cognome_discente} ${discente.nome_discente}`}
              </p>
            </div>
            <p
              style={{
                width: "220px",
                fontSize: "1.2rem",
                fontWeight: "bold",
                color: Color.DARK_GREY,
              }}
            >
              {gruppo.cf}
            </p>
            <p
              style={{
                width: "200px",
                fontSize: "1.2rem",
                fontWeight: "bold",
                color: Color.DARK_GREY,
              }}
            >
              {discente.sede_azienda}
            </p>
            <div
              style={{
                width: "180px",
                fontSize: "1rem",
                marginRight: "4.5rem",
              }}
            >
              <BadgeStatoAttestato attestato={gruppo.rappresentativo} />
            </div>
            <p aria-hidden="true">
              <ChevronDown color={Color.WHITE} height={32} width={32} />
            </p>
          </div>
        </Disclosure>
        <DisclosureContent>
          {(isOpen || hasOpened) && (
            <>
              <SearchInput style={{ width: "90%" }}>
                <div
                  style={{
                    position: "relative",
                  }}
                >
                  <input
                    name="searchPartecipantiCorso"
                    type="text"
                    placeholder="Ricerca i corsi"
                    aria-label="Ricerca i corsi"
                    onChange={(e) => setSearchCorso(e.target.value)}
                  />
                  <SearchButton>
                    <Search color={Color.WHITE} size={16} width={16} />
                  </SearchButton>
                </div>
              </SearchInput>
              {filteredAttestati.length === 0 ? (
                // La scheda di un discente ha sempre almeno una riga: se non
                // ne mostra nessuna e' la ricerca sul corso a escluderle.
                <StatoVuoto
                  icona={GraduationCap}
                  titolo={t("attestati-tab-corso-vuoto-filtri")}
                  suggerimento={t(
                    "attestati-dipendente-ricerca-vuoto-suggerimento"
                  )}
                />
              ) : (
                <div
                  style={{
                    display: "flex",
                    width: "100%",
                    flexDirection: "column",
                    gap: ".5rem",
                  }}
                >
                  <TableAttestatiHeader>
                    <p
                      style={{
                        width: "430px",
                      }}
                    >
                      Corso
                    </p>
                    <p>Descrizione</p>
                    <p>Stato</p>
                    <p>Info</p>
                    <p
                      style={{
                        justifySelf: "center",
                      }}
                    >
                      Attestati
                    </p>
                    <p></p>
                  </TableAttestatiHeader>
                  {filteredAttestati.map((a) => {
                    const prenotato = a.metadata.data_prenotazione !== undefined;
                    const assunto = a.isHired;
                    const corsoInAbbonamento = a.isSubscribed;
                    return (
                      <TableAttestatiRow key={`attestato_${a.id}`}>
                        <p
                          style={{
                            fontSize: "1rem",
                            width: "430px",
                          }}
                        >
                          {nomeCorso(a, catalogo)}
                        </p>
                        <p>{a.metadata.descrizione_corso}</p>
                        <BadgeStatoAttestato attestato={a} />
                        <InfoPrenotazione attestato={a} />
                        <ScaricaAttestato attestato={a} />
                        {prenotato || !assunto ? null : corsoInAbbonamento ? (
                          <div
                            style={{
                              textAlign: "center",
                              cursor: "pointer",
                            }}
                            onClick={() => prenota(a)}
                          >
                            <Suggerimento
                              testo={t("attestati-aiuto-prenota-dipendente")}
                            >
                              <CalendarDays color={Color.BLUE} />
                            </Suggerimento>
                          </div>
                        ) : (
                          <Ariakit.HovercardProvider>
                            <Ariakit.HovercardAnchor render={<div />}>
                              <TriangleAlert
                                color={Color.RED}
                                height={24}
                                width={24}
                              />
                            </Ariakit.HovercardAnchor>
                            <Ariakit.Hovercard
                              fixed
                              gutter={10}
                              style={{
                                backgroundColor: "var(--red500)",
                                color: "var(--white)",
                                padding: "1em",
                                borderRadius: "8px",
                                width: "28vw",
                              }}
                            >
                              <p>{t("attestati-corso-non-in-abbonamento")}</p>
                            </Ariakit.Hovercard>
                          </Ariakit.HovercardProvider>
                        )}
                      </TableAttestatiRow>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </DisclosureContent>
      </Ariakit.DisclosureProvider>
    </>
  );
}
